import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showSuccess, showError } from '../../services/alert';

const InsumoForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [insumo, setInsumo] = useState({
    nome: '',
    tipo: '',
    unidadeMedida: '',
    estoqueAtual: 0,
    estoqueMinimo: 0,
    valorMedio: 0,
    fornecedorId: '',
    dataValidade: '',
    lote: '',
  });
  const [loading, setLoading] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);

  useEffect(() => {
    api.get('/fornecedor')
      .then(res => setFornecedores(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFornecedores([]));

    if (isEditing) {
      setLoading(true);
      api.get(`/insumos/${id}`)
        .then(res => {
          const d = res.data;
          setInsumo({
            nome: d.nome,
            tipo: d.tipo,
            unidadeMedida: d.unidadeMedida,
            estoqueAtual: d.estoqueAtual,
            estoqueMinimo: d.estoqueMinimo || 0,
            valorMedio: d.valorMedio || 0,
            fornecedorId: d.fornecedorId || '',
            dataValidade: d.dataValidade?.substring(0, 10) || '',
            lote: d.lote || '',
          });
        })
        .catch(() => showError('Erro ao carregar insumo.'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInsumo(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...insumo,
        estoqueAtual: Number(insumo.estoqueAtual || 0),
        estoqueMinimo: Number(insumo.estoqueMinimo || 0),
        valorMedio: Number(insumo.valorMedio || 0),
        fornecedorId: insumo.fornecedorId ? Number(insumo.fornecedorId) : null,
        dataValidade: insumo.dataValidade || null
      };

      if (isEditing) {
        await api.put(`/insumos/${id}`, payload);
        showSuccess('Insumo atualizado com sucesso.');
      } else {
        await api.post('/insumos', payload);
        showSuccess('Insumo criado com sucesso.');
      }
      navigate('/insumos');
    } catch {
      showError('Erro ao salvar insumo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate('/insumos')}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>
          <h5 className="mb-0">{isEditing ? 'Editar Insumo' : 'Novo Insumo'}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Nome *</label>
              <input
                type="text"
                name="nome"
                value={insumo.nome}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Tipo *</label>
              <select
                name="tipo"
                value={insumo.tipo}
                onChange={handleChange}
                className="form-select"
                required
                disabled={loading}
              >
                <option value="">Selecione...</option>
                <option value="Semente">Semente</option>
                <option value="Adubo">Adubo</option>
                <option value="Defensivo">Defensivo</option>
                <option value="Fertilizante">Fertilizante</option>
                <option value="Diesel">Diesel</option>
                <option value="Peças">Peças</option>
                <option value="Ração">Ração</option>
                <option value="Vacina">Vacina</option>
                <option value="Medicamento">Medicamento</option>
                <option value="Outros">Outros</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Unidade de Medida *</label>
              <input
                type="text"
                name="unidadeMedida"
                value={insumo.unidadeMedida}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Estoque Atual</label>
              <input
                type="number"
                name="estoqueAtual"
                value={insumo.estoqueAtual}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Estoque Mínimo</label>
              <input
                type="number"
                name="estoqueMinimo"
                value={insumo.estoqueMinimo}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Valor Médio</label>
              <CurrencyInput
                name="valorMedio"
                value={insumo.valorMedio}
                onChange={handleChange}
                className="form-control"
                placeholder="0,00"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Fornecedor</label>
              <select
                name="fornecedorId"
                value={insumo.fornecedorId}
                onChange={handleChange}
                className="form-select"
                disabled={loading}
              >
                <option value="">Sem fornecedor padrão</option>
                {fornecedores.map(f => (
                  <option key={f.id} value={f.id}>{f.nome}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Data de Validade</label>
              <input
                type="date"
                name="dataValidade"
                value={insumo.dataValidade}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Lote</label>
              <input
                name="lote"
                value={insumo.lote}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-12 d-flex justify-content-end mt-3">
              <button type="submit" className="btn btn-success me-2 d-flex align-items-center gap-1" disabled={loading}>
                <i className="bi bi-save"></i>
                <span>{loading ? 'Salvando...' : 'Salvar'}</span>
              </button>
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-1"
                onClick={() => navigate('/insumos')}
                disabled={loading}
              >
                <i className="bi bi-x-circle"></i>
                <span>Cancelar</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InsumoForm;
